/**
 * This module handle all view components of GEO
 *
 * Components are widget available on application , like Legends Controls,
 * Search, etc. Each component can accept specific properties (in the properties
 * parameters)  and can propose extensions points for vizualisation.
 *
 * One can handle visibility of each component in the application,at startup.
 * For example:
 *
 * ```
 *
 *  var options= {
 *     components: {
 *              Header: {visible: false},
 *              RightPanel: {visible: false}
 *       }
 *  };
 *  geoApp = new geoApi.GeoApp("mapDiv", urlApp, options);
 * ```
 * One has to note that disponbility of components in the app depends on each
 * layout.
 *
 * Some components have inner components, for example Header
 *
 * ## Available components by layout
 *
 * ### Gabarit GP1
 * 
 *  - [MapScaleLine](MapScaleLine)
 *  - [MapBasicControls](MapBasicControls)
 *  - [MapGeolocation](MapGeolocation)
 *  - [LayerControl](LayerControl)
 *  - [Header](Header)
 *  - [SearchBox](SearchBox)
 *    -  [BaseLayerSwitcher](BaseLayerSwitcher)
 *    -  [UserInfoHeader](UserInfoHeader)
 *    -  [PrintLink](PrintLink)
 *    -  [FunctionsLauncher](FunctionsLauncher)
 *    -  [Logo](Logo)
 *    -  [Title](Title)
 *  - [SidePanel](SidePanel)
 *    -  [SearchResultPanel](SearchResultPanel)
 *    -  [ExplorePanel](ExplorePanel)
 *    -  [InfoSheetPanel](InfoSheetPanel)
 *    -  [AnalysesResultsPanel](AnalysesResultsPanel)
 *    -  [StatsResultsPanel](StatsResultsPanel)
 *
 * ### Gabarit PRO
 * 
 *  Preview of main components view  for Gabarit PRO:
 *  ![](/images/PROcomponents.png)
 * 
 *  - [Header](Header)
 *    -  [SearchBox](SearchBox)
 *    -  [UserInfoHeader](userinfoheader)
 *    -  [PrintLink](PrintLink)
 *    -  [Logo](Logo)
 *    -  [Title](Title)
 *  - [RightPanel](RightPanel)
 *    -  [ApplicationRightPanel](ApplicationRightPanel)
 *    -  [GlobalParametersRightPanel](GlobalParametersRightPanel)
 *    -  [EditionRightPanel](EditionRightPanel)
 *    -  [MapRightPanel](MapRightPanel)
 *    -  [AnalysesRightPanel](AnalysesRightPanel)
 *    -  [StatsRightPanel](StatsRightPanel)
 *    -  [SearchResultsRightPanel](SearchResultsRightPanel)
 *    -  [PackPersoRightPanel](PackPersoRightPanel)
 *  - [FunctionsLauncher](FunctionsLauncher)
 *  - [MapTools](MapTools)
 *    - [MapIdentifierTool](MapIdentifierTool) ("i")
 *    - [LayerQueryTool](LayerQueryTool)
 *    - [MapBasicControls](MapBasicControls)
 *    - [MapGeolocation](MapGeolocation)
 *    - [MapScaleLine](MapScaleLine)
 *    - [MapScalePicker](MapScalePicker)
 *  - [BottomPanel](BottomPanel)
 *
 * @module geo/components
 * @since v2.0.1 
 * @preferred
 * 
 */
/**
 * @preferred
 */
import { ExtensionType } from "../types/extensions";
import {
  InfosheetExtension,
  InfosheetExtensionProperties
} from "../types/extensions/infosheet";

import { BottomPanel } from "./bottom.panel";
import { Component } from "./component";
import { FunctionsLauncher } from "./functions.launcher";
import {
  Header,
  Logo,
  PrintLink,
  SearchBox,
  Title,
  UserInfoHeader
} from "./header";
import { LayerControl } from "./layer.control";
import { MapTools, MapScaleLine, MapBasicControls, MapGeolocation, MapScalePicker, MapIdentifierTool, MapSelectionTools, MapToolsControl, MeasureTool } from "./map.tools";
import {
  AnalysesRightPanel,
  ApplicationRightPanel,
  EditionRightPanel,
  GlobalParametersRightPanel,
  MapRightPanel,
  PackPersoRightPanel,
  RightPanel,
  SearchResultsRightPanel,
  StatsRightPanel
} from "./right.panel";
import {
  AnalysesResultsPanel,
  ExplorePanel,
  InfoSheetPanel,
  SearchResultPanel,
  SidePanel,
  StatsResultsPanel
} from "./side.panel";

/**
 * show and handle way of switching base layers.
 * @since v2.0.1
 */
export class BaseLayerSwitcher extends Component {}



/**
 * component Infosheet. 
 * Only for adding extension to infosheet.
 * 
 * @since 2.1.0
 */
export class Infosheet extends Component {
  defaultExtensionType = ExtensionType.INFOSHEET;

  public buildExtension(
    properties: InfosheetExtensionProperties
  ): InfosheetExtension {
    const type = this.defaultExtensionType;
    const props = { ...properties };
    if (properties.enableOptions && !properties.enable) {
      const opt = properties.enableOptions;
      props.enable = (f, infosheet, search, isc) => {
        return (
          (!opt.infosheetId || opt.infosheetId === infosheet.id) &&
          (!opt.searchId || opt.searchId === search.id) &&
          (!opt.infosheetName || opt.infosheetName === infosheet.name) &&
          (!opt.searchName || opt.searchName === search.name)
        );
      };
    }
    return { type, properties: props } as InfosheetExtension;
  }
}

export { Component, FunctionsLauncher };
/**
 * @private
 * @internalapi
 */
export const ComponentsStore: any = {
  MapScaleLine,
  MapBasicControls,
  MapGeolocation,
  LayerControl,
  Header,
  SearchBox,
  BaseLayerSwitcher,
  UserInfoHeader,
  PrintLink,
  FunctionsLauncher,
  Logo,
  Title,
  SidePanel,
  SearchResultPanel,
  ExplorePanel,
  InfoSheetPanel,
  AnalysesResultsPanel,
  StatsResultsPanel,
  MapScalePicker,
  RightPanel,
  ApplicationRightPanel,
  GlobalParametersRightPanel,
  EditionRightPanel,
  MapRightPanel,
  AnalysesRightPanel,
  StatsRightPanel,
  SearchResultsRightPanel,
  PackPersoRightPanel,
  MapIdentifierTool,
  MapSelectionTools,
  MapToolsControl,
  MapTools,
  BottomPanel,
  Infosheet,
  MeasureTool
};

/**
 * Loader for accessor of component
 * 
 * see [[geo.Application.components]]
 *
 * @internalapi
 * @since 2.1.0
 */
export class ComponentInstanceLoader {
  static getInstance(name: string, ...args: any[]): Component {
    if (!ComponentsStore[name]) {
      throw Error("Unknown component type :" + name);
    }
    const instance = Object.create(ComponentsStore[name].prototype);
    instance.constructor.apply(instance, args);
    return instance;
  }
}

/**
 * @internalapi
 */
export interface HashComponents {
  [name: string]: Component;
}
